import { Plus, X } from "lucide-react";
import { distanceSaid, STATUS_SAID, type PlaceHit } from "../lib/place-search.ts";
import { Pill } from "../ui/index.ts";

/** What Add does next, said once, so the button is not the only thing explaining itself. */
export const ADD_TAKES_YOU =
  "Adding it opens the trip's Places page, with its name and the hours Google knows already filled in.";

/**
 * A place Google found, looked at before it is added: the place view's shape — close at the left,
 * the name between — with **Add** standing where Like and Visited stand there.
 *
 * It shows only what the search already came back with: the name, how far it is, and whatever
 * Google says about the place trading at all. Nothing here is read from Google, so looking
 * through results costs nothing; the one paid read of a place happens on the Places page, once a
 * member has actually chosen it. That is also why the hours are not here: they exist only in that
 * read, and paying for them to be glanced at would be paying for every result rejected too.
 *
 * No menu: Edit and Delete belong to a place the trip has, and this one is not on the trip yet.
 * The header's third column is simply left empty, so the name stays centred as it is in the view.
 *
 * Nothing is written from here either. Add carries the place's id to the Places page and that
 * page's form is where a place is made, which keeps one create path for the whole trip.
 */
export function FoundPlaceView({
  hit,
  onAdd,
  onClose,
}: {
  /** The result as the search gave it: name, position, and distance or status where there is one. */
  hit: PlaceHit;
  /** Take this one on to the Places page to be added. */
  onAdd: () => void;
  onClose: () => void;
}) {
  const far = hit.distanceM === undefined ? null : distanceSaid(hit.distanceM);
  const flag = hit.status ? STATUS_SAID[hit.status] : null;

  return (
    <section className="poi-editor poi-view poi-found-view" aria-label={hit.name}>
      <header className="poi-editor-head poi-view-head">
        <button
          type="button"
          className="icon-button poi-editor-close"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} strokeWidth={1.75} aria-hidden="true" />
        </button>
        {/* The header is a three-column grid, so the name stays centred over an empty third. */}
        <h2 className="poi-editor-title poi-view-name">{hit.name}</h2>
      </header>
      <div className="poi-editor-body poi-view-body">
        {(flag || far) && (
          <div className="poi-view-meta">
            {flag && <Pill tone="wait">{flag}</Pill>}
            {far && <p className="small">{far}</p>}
          </div>
        )}
        <p className="small muted">{ADD_TAKES_YOU}</p>
        {/* One button, across the width, where the place view's two sit. */}
        <div className="poi-view-actions" role="group" aria-label={`${hit.name} controls`}>
          <button type="button" className="button primary poi-found-add" onClick={onAdd}>
            <Plus size={20} strokeWidth={2} aria-hidden="true" />
            Add
          </button>
        </div>
      </div>
    </section>
  );
}
